import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Building2, LayoutPanelLeft, ListChecks, CheckCircle2, Loader2, Home, Search } from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

const unitTypes = ["Residential", "Commercial", "Retail", "Office"];
const unitStatuses = ["Available", "Occupied", "Maintenance"];
const allAmenities = [
    "WiFi", "Parking", "Backup Generator", "Borehole",
    "Swimming Pool", "Gym", "24/7 Security", "CCTV",
    "Balcony", "Furnished", "Elevator",
];

export const UnitRegistration: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Store connectivity for existing units
    const { properties, fetchProperties, fetchUnits, units } = useLandlordStore();
    const { warning, success, error: toastError } = useToast();

    // Init from URL params
    const initialPropId = searchParams.get('propertyId') || '';
    const initialPropName = searchParams.get('propertyName') || 'Property';

    const [propertyId, setPropertyId] = useState(initialPropId);
    const [unitName, setUnitName] = useState("");
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>("Available");
    const [baseRent, setBaseRent] = useState("");
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
    
    const [isLoading, setIsLoading] = useState(false);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (properties.length === 0) {
            fetchProperties();
        }
    }, [properties, fetchProperties]);

    // Fetch units whenever propertyId changes
    useEffect(() => {
        if (propertyId) {
            loadUnits(propertyId);
        }
    }, [propertyId]);

    const loadUnits = async (pid: string) => {
        setLoadingUnits(true);
        try {
            await fetchUnits(pid);
        } catch (e) {
            console.error("Failed to fetch contextual units", e);
        } finally {
            setLoadingUnits(false);
        }
    };

    const handleToggleAmenity = (amenity: string) => {
        setSelectedAmenities(prev =>
            prev.includes(amenity)
                ? prev.filter(a => a !== amenity)
                : [...prev, amenity]
        );
    };

    const handleRegisterUnit = async () => {
        if (!propertyId || !unitName || !selectedType) {
            warning('Incomplete form', 'Please provide Property ID, Unit Name, and select a Unit Type.');
            return;
        }

        setIsLoading(true);
        try {
            const unitData = {
                propertyId,
                unitName,
                unitType: selectedType,
                status: selectedStatus,
                baseRent: parseFloat(baseRent) || 0,
                amenities: selectedAmenities,
            };

            await apiClient.post("/units", unitData);
            success('Unit registered', `Unit ${unitName} has been registered successfully.`);
            navigate(-1);
        } catch (error: any) {
            console.error("Error registering unit:", error);
            const errorMessage = error.response?.data?.error || "An unexpected error occurred.";
            toastError('Registration failed', errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const inputClass = "w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium";
    const labelClass = "block text-sm font-bold text-light-text dark:text-dark-text mb-2";

    // Filter available units to display on the side
    const availableUnits = units.filter(u => 
        u.property_id === propertyId && 
        u.status === 'Available' &&
        u.unit_number.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-7xl mx-auto pb-12 space-y-6">
            <div className="flex items-center justify-between bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                    >
                        <ArrowLeft size={24} className="text-primary dark:text-golden" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text border-b-2 border-primary/20 pb-1 inline-block">Register New Unit</h1>
                        {initialPropName && <p className="text-sm font-medium text-light-subtle dark:text-dark-subtle mt-1">Belongs to: {initialPropName}</p>}
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Form Section */}
                <div className="flex-1 bg-light-card dark:bg-dark-card p-6 lg:p-8 rounded-2xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
                    
                    {/* Details */}
                    <div className="space-y-4">
                        <h2 className="text-lg font-bold text-primary flex items-center gap-2 mb-6">
                            <Building2 size={24} /> General Information
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className={labelClass}>Select Property</label>
                                <select
                                    value={propertyId}
                                    onChange={(e) => setPropertyId(e.target.value)}
                                    disabled={!!initialPropId}
                                    className={`${inputClass} ${initialPropId ? 'opacity-70 cursor-not-allowed bg-light-bg dark:bg-dark-bg' : ''}`}
                                >
                                    <option value="" disabled>-- Select Property --</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Unit Label / Designator</label>
                                <input
                                    type="text"
                                    value={unitName}
                                    onChange={(e) => setUnitName(e.target.value)}
                                    placeholder="e.g., Block A - 104"
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Mandatory Rent (KES)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={baseRent}
                                    onChange={(e) => setBaseRent(e.target.value)}
                                    placeholder="e.g., 50000"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Classifications */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-light-border dark:border-dark-border">
                        <div className="space-y-4">
                            <h2 className="text-lg font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                                <LayoutPanelLeft size={20} className="text-primary" /> Target Demographic
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {unitTypes.map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedType(type)}
                                        className={`px-4 py-2 rounded-xl font-bold transition-all border-2 ${
                                            selectedType === type
                                                ? "bg-primary/10 border-primary text-primary shadow-sm"
                                                : "bg-light-input dark:bg-dark-input border-transparent text-light-subtle dark:text-dark-subtle hover:border-primary/30 hover:text-light-text dark:hover:text-dark-text"
                                        }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h2 className="text-lg font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                                Status Tracking
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {unitStatuses.map(status => (
                                    <button
                                        key={status}
                                        onClick={() => setSelectedStatus(status)}
                                        className={`px-4 py-2 rounded-xl font-bold transition-all border-2 ${
                                            selectedStatus === status
                                                ? "bg-primary/10 border-primary text-primary shadow-sm"
                                                : "bg-light-input dark:bg-dark-input border-transparent text-light-subtle dark:text-dark-subtle hover:border-primary/30 hover:text-light-text dark:hover:text-dark-text"
                                        }`}
                                    >
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Amenities */}
                    <div className="space-y-4 pt-4 border-t border-light-border dark:border-dark-border">
                        <h2 className="text-lg font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                            <ListChecks size={20} className="text-primary" /> Unit-Included Amenities
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {allAmenities.map(amenity => {
                                const isSelected = selectedAmenities.includes(amenity);
                                return (
                                    <button
                                        key={amenity}
                                        onClick={() => handleToggleAmenity(amenity)}
                                        className={`px-4 py-2 rounded-xl font-bold transition-all border-2 flex items-center gap-2 ${
                                            isSelected
                                                ? "bg-primary/10 border-primary text-primary shadow-sm"
                                                : "bg-light-input dark:bg-dark-input border-transparent text-light-subtle dark:text-dark-subtle hover:border-primary/30"
                                        }`}
                                    >
                                        {isSelected && <CheckCircle2 size={16} />}
                                        {amenity}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Submit Action */}
                    <div className="pt-8 border-t border-light-border dark:border-dark-border">
                        <button
                            onClick={handleRegisterUnit}
                            disabled={isLoading || !propertyId || !unitName || !selectedType}
                            className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:opacity-90 disabled:opacity-50 disabled:shadow-none transition-all shadow-md"
                        >
                            {isLoading ? (
                                <><Loader2 className="animate-spin" size={20} /> Registering...</>
                            ) : (
                                <><Save size={20} /> Create Record</>
                            )}
                        </button>
                    </div>
                </div>

                {/* Sidebar Context - Available Units */}
                {propertyId && (
                    <div className="w-full lg:w-80 flex flex-col gap-4">
                        <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl shadow-sm border border-light-border dark:border-dark-border flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
                                    <Home size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-light-text dark:text-dark-text">Currently Available</h3>
                                    <p className="text-xs font-medium text-light-subtle dark:text-dark-subtle">Check listings before creating</p>
                                </div>
                            </div>

                            <div className="relative mb-6">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={16} />
                                <input 
                                    type="text"
                                    placeholder="Filter by label..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-sm font-medium outline-none focus:border-primary/50"
                                />
                            </div>

                            {loadingUnits ? (
                                <div className="flex flex-col items-center justify-center py-12 text-light-subtle dark:text-dark-subtle">
                                    <Loader2 size={24} className="animate-spin mb-2" />
                                    <p className="text-sm font-medium">Fetching register...</p>
                                </div>
                            ) : availableUnits.length > 0 ? (
                                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                    {availableUnits.map(unit => (
                                        <div key={unit.id} className="p-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl">
                                            <div className="flex justify-between items-center mb-1">
                                                <h4 className="font-bold text-light-text dark:text-dark-text">Unit {unit.unit_number}</h4>
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 uppercase">Available</span>
                                            </div>
                                            <p className="text-xs font-medium text-light-subtle dark:text-dark-subtle">KES {unit.rent_amount?.toLocaleString()} • {unit.unit_type || 'Standard'}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 px-4 bg-light-bg dark:bg-dark-bg rounded-xl border border-light-border dark:border-dark-border border-dashed">
                                    <p className="text-sm font-bold text-light-text dark:text-dark-text mb-1">No Available Units</p>
                                    <p className="text-xs font-medium text-light-subtle dark:text-dark-subtle">Looks like you're fully occupied or haven't created any unassigned spaces.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
